import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { ToastItem, ToastViewport, ToastProps } from '../components/ui/Toast';
import { ConfirmDialog, ConfirmDialogProps } from '../components/ui/ConfirmDialog';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface NotificationContextType {
  showToast: (title: string, description?: string, type?: ToastType, duration?: number) => void;
  showSuccess: (title: string, description?: string) => void;
  showError: (title: string, description?: string) => void;
  showWarning: (title: string, description?: string) => void;
  showInfo: (title: string, description?: string) => void;
  showConfirm: (options: Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm' | 'onCancel'>) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

interface ToastItemWithId extends Omit<ToastProps, 'onClose'> {
  id: string;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItemWithId[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    props: Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm' | 'onCancel'>;
    resolve: (value: boolean) => void;
  } | null>(null);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((
    title: string,
    description?: string,
    type: ToastType = 'info',
    duration = 5000
  ) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: ToastItemWithId = {
      id,
      title,
      description,
      type,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);
  }, []);

  const showSuccess = useCallback((title: string, description?: string) => {
    showToast(title, description, 'success');
  }, [showToast]);

  const showError = useCallback((title: string, description?: string) => {
    showToast(title, description, 'error');
  }, [showToast]);

  const showWarning = useCallback((title: string, description?: string) => {
    showToast(title, description, 'warning');
  }, [showToast]);

  const showInfo = useCallback((title: string, description?: string) => {
    showToast(title, description, 'info');
  }, [showToast]);

  const showConfirm = useCallback((
    options: Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm' | 'onCancel'>
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmDialog({ props: options, resolve });
    });
  }, []);

  const handleConfirmDialogClose = useCallback((confirmed: boolean) => {
    if (confirmDialog) {
      confirmDialog.resolve(confirmed);
      setConfirmDialog(null);
    }
  }, [confirmDialog]);

  const value: NotificationContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirm,
  };

  return (
    <NotificationContext.Provider value={value}>
      <Toast.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            {...toast}
            onClose={removeToast}
          />
        ))}
        <ToastViewport />
      </Toast.Provider>

      {confirmDialog && (
        <ConfirmDialog
          {...confirmDialog.props}
          open={true}
          onOpenChange={(open) => !open && handleConfirmDialogClose(false)}
          onConfirm={() => handleConfirmDialogClose(true)}
          onCancel={() => handleConfirmDialogClose(false)}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

// Convenience hooks for specific types
export const useToast = () => {
  const { showToast, showSuccess, showError, showWarning, showInfo } = useNotification();
  return { showToast, showSuccess, showError, showWarning, showInfo };
};

// Non-throwing toast — safe to call even when no NotificationProvider is mounted (returns no-ops).
// For leaf components that may render in isolation (e.g. unit tests) but still surface a toast in-app.
const NOOP = () => { /* no provider — silently ignore */ };
export const useOptionalToast = () => {
  const ctx = useContext(NotificationContext);
  return {
    showToast: ctx?.showToast ?? NOOP,
    showSuccess: ctx?.showSuccess ?? NOOP,
    showError: ctx?.showError ?? NOOP,
    showWarning: ctx?.showWarning ?? NOOP,
    showInfo: ctx?.showInfo ?? NOOP,
    /* Optional confirm: outside a provider it resolves TRUE, matching the other no-ops here — a missing
       provider must not silently cancel an action the user asked for. */
    showConfirm: ctx?.showConfirm ?? (async () => true),
  };
};

export const useConfirm = () => {
  const { showConfirm } = useNotification();
  return showConfirm;
};